import type { Rng } from "./rng";
import type { Direction, VocabEntry } from "./types";

export type Choice = {
  /**
   * Anzeigeform der Antwort-Option.
   *
   * Vor Issue #95 hieß das Feld `german` weil es immer eine deutsche
   * Übersetzung war. Mit Phase 2 kann es auch ein englisches Fachwort sein
   * (DE→EN-Modus). Den Property-Namen behalten wir, damit Phase 2 die
   * QuizScreen/ChoiceButton-Komponenten nicht anrührt — Phase 3 räumt
   * Naming + Type-Discrimination auf.
   */
  german: string;
  correct: boolean;
};

const DISTRACTOR_COUNT = 3;
const SAME_LETTER_TARGET = 2;

/**
 * Drei deutsche Distraktoren für einen englischen Prompt (EN→DE).
 * Bevorzugt 2 same-letter (Spalte `letter` = English-Letter), Rest aus dem
 * Pool ohne diesen Buchstaben.
 */
export function pickGermanDistractors(
  target: VocabEntry,
  vocab: readonly VocabEntry[],
  rng: Rng,
): string[] {
  const candidates = vocab.filter((v) => v.english !== target.english && v.german !== target.german);
  if (candidates.length < DISTRACTOR_COUNT) {
    throw new Error("vocab too small to generate distractors");
  }

  const sameLetter = candidates.filter((v) => v.letter === target.letter);
  const restPool = candidates.filter((v) => v.letter !== target.letter);

  const picks: VocabEntry[] = [];
  const taken = new Set<string>();

  const takeFrom = (pool: VocabEntry[], howMany: number) => {
    const fresh = pool.filter((v) => !taken.has(v.german));
    const n = Math.min(howMany, fresh.length);
    const sampled = rng.sample(fresh, n);
    for (const v of sampled) {
      picks.push(v);
      taken.add(v.german);
    }
  };

  takeFrom(sameLetter, SAME_LETTER_TARGET);
  takeFrom(restPool, DISTRACTOR_COUNT - picks.length);
  if (picks.length < DISTRACTOR_COUNT) {
    takeFrom(candidates, DISTRACTOR_COUNT - picks.length);
  }

  if (picks.length < DISTRACTOR_COUNT) {
    throw new Error("could not assemble 3 unique distractors");
  }
  return picks.map((p) => p.german);
}

/**
 * Drei englische Distraktoren für einen deutschen Prompt (DE→EN). Bevorzugt
 * same-letter über die *englische* Letter-Spalte — spiegelt das EN→DE-
 * Verhalten und nutzt die kanonische Letter-Indizierung der Vokabelliste.
 */
export function pickEnglishDistractors(
  target: VocabEntry,
  vocab: readonly VocabEntry[],
  rng: Rng,
): string[] {
  const candidates = vocab.filter((v) => v.english !== target.english && v.german !== target.german);
  if (candidates.length < DISTRACTOR_COUNT) {
    throw new Error("vocab too small to generate distractors");
  }

  const sameLetter = candidates.filter((v) => v.letter === target.letter);
  const restPool = candidates.filter((v) => v.letter !== target.letter);

  const picks: VocabEntry[] = [];
  const taken = new Set<string>();

  const takeFrom = (pool: VocabEntry[], howMany: number) => {
    const fresh = pool.filter((v) => !taken.has(v.english));
    const n = Math.min(howMany, fresh.length);
    const sampled = rng.sample(fresh, n);
    for (const v of sampled) {
      picks.push(v);
      taken.add(v.english);
    }
  };

  takeFrom(sameLetter, SAME_LETTER_TARGET);
  takeFrom(restPool, DISTRACTOR_COUNT - picks.length);
  if (picks.length < DISTRACTOR_COUNT) {
    takeFrom(candidates, DISTRACTOR_COUNT - picks.length);
  }

  if (picks.length < DISTRACTOR_COUNT) {
    throw new Error("could not assemble 3 unique distractors");
  }
  return picks.map((p) => p.english);
}

/**
 * Legacy-Alias für pre-#95 Aufrufer. Verhält sich identisch zu
 * `pickGermanDistractors` (EN→DE-Distraktoren). Wird vom Test-Suite und
 * von ChoiceScreen genutzt bis Phase 3 die Call-Sites direction-aware macht.
 */
export const pickDistractors = pickGermanDistractors;

export function buildChoices(
  target: VocabEntry,
  vocab: readonly VocabEntry[],
  rng: Rng,
  direction: Direction = "en_de",
): Choice[] {
  const distractors =
    direction === "en_de"
      ? pickGermanDistractors(target, vocab, rng)
      : pickEnglishDistractors(target, vocab, rng);
  const correctValue = direction === "en_de" ? target.german : target.english;
  const choices: Choice[] = distractors.map((german) => ({ german, correct: false }));
  const correctIndex = rng.intBetween(0, choices.length);
  choices.splice(correctIndex, 0, { german: correctValue, correct: true });
  return choices;
}
